<!--
 * @author xuyiyuan
 * @date 2023-08-17 09:52
 * @description  库存管理
-->
<template>
  <lsTableLayout>
    <div slot="search">
      <lsSearchForm :formList="formList" @search="onSearch">
        <a-select slot="organizationType" v-decorator="['organizationType']" allowClear>
          <a-select-option v-for="(item) in ORGANIZE_TYPE_LIST" :key="item.key">{{ item.name }}</a-select-option>
        </a-select>
        <a-select slot="status" v-decorator="['status']" allowClear>
          <a-select-option v-for="(item) in STATUS_LIST" :key="item.key">{{ item.name }}</a-select-option>
        </a-select>
      </lsSearchForm>
    </div>
    <div slot="table" class="table-container">
      <ls-api-table
        ref="dataTable"
        :columns="columns"
        :tableProps="tableProps"
        :multi-select="true"
        rowKey="id"
        :scroll="{ fixScreen: true, x: 1300 }"
        :toolBox="{
                density: true, // 传入String时 初始化默认表格密度
                fullScreen: false,  // 是否需要全屏
                configKey: $TableKey.INVENTORY_ORGANIZATION_LIST, // 表格方案配置唯一值
              }"
      >
        <template #organizationType="{ text }">
          <span>{{ ORGANIZE_TYPE[text] }}</span>
        </template>
        <template #companyName="{ record }">
          <span>{{ record.organizationType === ORGANIZE_TYPE.linsy.key ? record.companyName : record.supplierName }}</span>
        </template>
        <template slot="status" slot-scope="{ text }">
          <a-badge :status="text | statusTypeFilter" :text="text | statusFilter" />
        </template>
        <!-- 工作日历 -->
        <template #calendarName="{ record }">
          <span v-if="!isEdit || currentId !== record.id">{{ record.calendarName }}</span>
          <a-select v-if="isEdit && currentId === record.id" v-model="record.calendarId" size="small">
            <a-select-option v-for="(item) in calendarIdList" :key="item.realValue">{{ item.label }}</a-select-option>
          </a-select>
        </template>
        <!-- 编码主组织 -->
        <template #masterOrganizationName="{ record }">
          <span v-if="!isEdit || currentId !== record.id">{{ record.masterOrganizationName }}</span>
          <a-select v-if="isEdit && currentId === record.id" v-model="record.masterOrganizationCode" size="small">
            <a-select-option v-for="(item) in inventOrg" :key="item.realValue">{{ item.label }}</a-select-option>
          </a-select>
        </template>
        <!-- BOM组织 -->
        <template #bomOrganizationName="{ record }">
          <span v-if="!isEdit || currentId !== record.id">{{ record.bomOrganizationName }}</span>
          <a-select v-if="isEdit && currentId === record.id" v-model="record.bomOrganizationCode" size="small">
            <a-select-option v-for="(item) in inventOrg" :key="item.realValue">{{ item.label }}</a-select-option>
          </a-select>
        </template>
        <template #updateTime="{text}">
          <span>{{ text && $moment(text).format('YYYY-MM-DD HH:mm') }}</span>
        </template>
        <template #createTime="{text}">
          <span>{{ text && $moment(text).format('YYYY-MM-DD HH:mm') }}</span>
        </template>
        <!-- 操作 -->
        <template #operation="{ record }">
          <a v-if="!isEdit || currentId !== record.id" @click="handleEdit(record) " v-hasPermi="['mes:tSysInventoryOrganization:edit']">编辑</a>
          <a v-if="isEdit && currentId === record.id" @click="handleSave(record)">保存</a>
          <a v-if="isEdit && currentId === record.id" @click="handleCancel(record)" class="ml10">取消</a>
        </template>
      </ls-api-table>
    </div>
  </lsTableLayout>
</template>

<script>
import { mapState } from 'vuex'
import { ORGANIZE_TYPE, STATUS, STATUS_LIST, ORGANIZE_TYPE_LIST } from './enum'
import { apiTSysInventoryOrganizationList, apiRoutingCalendarPullDownList, apiTSysInventoryOrganizationUpdate } from '@/api/mdm/supplychain/inventoryOrganization'
import { generateLsTableResData } from '@/utils/util.js';

const statusMap = {
  2: {
    status: 'processing',
    text: '未启用'
  },
  1: {
    status: 'processing',
    text: '启用'
  },
  0: {
    status: 'default',
    text: '停用'
  }
}

export default {
  name: 'InventoryOrganization',
  components: {},
  data () {
    return {
      isEdit: false,
      currentId: "",
      ORGANIZE_TYPE, // 组织类型
      ORGANIZE_TYPE_LIST, // 组织类型列表
      STATUS, // 状态
      STATUS_LIST, // 状态列表
      formList: [
        {
          label: '组织编码',
          decorator: ['organizationNo'],
          type: 'input'
        },
        {
          label: '组织名称',
          decorator: ['organizationName'],
          type: 'input'
        },
        {
          label: '类型',
          type: 'organizationType'
        },
        {
          label: '状态',
          type: 'status'
        }
      ],
      // 表格配置
      tableProps: {
        requestApi: generateLsTableResData(apiTSysInventoryOrganizationList, true),
        pageCurrent: true
      },
      // 表头
      columns: [
        {
          title: '组织编码',
          dataIndex: 'organizationNo',
          stickyFixed: 'left',
          ellipsis: true,
          width: 100
        },
        {
          title: '组织名称',
          dataIndex: 'organizationName',
          stickyFixed: 'left',
          ellipsis: true,
          width: 150
        },
        {
          title: '类型',
          dataIndex: 'organizationType',
          scopedSlots: { customRender: 'organizationType' },
          stickyFixed: 'left',
          ellipsis: true,
          width: 80
        },
        {
          title: '归属主体',
          dataIndex: 'companyName',
          scopedSlots: { customRender: "companyName" },
          ellipsis: true,
          width: 250
        },
        {
          title: '状态',
          dataIndex: 'status',
          scopedSlots: { customRender: 'status' },
          ellipsis: true,
          width: 100
        },
        {
          title: '工作日历',
          dataIndex: 'calendarName',
          scopedSlots: { customRender: 'calendarName' },
          ellipsis: true,
          width: 150
        },
        {
          title: '编码主组织',
          dataIndex: 'masterOrganizationName',
          scopedSlots: { customRender: 'masterOrganizationName' },
          ellipsis: true,
          width: 150
        },
        {
          title: 'BOM组织',
          dataIndex: 'bomOrganizationName',
          scopedSlots: { customRender: 'bomOrganizationName' },
          ellipsis: true,
          width: 150
        },
        {
          title: '更新时间',
          width: 150,
          dataIndex: 'updateTime',
          scopedSlots: { customRender: "updateTime" }
        },
        {
          title: '更新人',
          dataIndex: 'dataModifyUserName',
          ellipsis: true,
          width: 100
        },
        {
          title: '创建时间',
          width: 150,
          dataIndex: 'createTime',
          scopedSlots: { customRender: "createTime" }
        },
        {
          title: '创建人',
          dataIndex: 'dataCreateUserName',
          ellipsis: true,
          width: 100
        },
        {
          title: '操作',
          width: 80,
          dataIndex: 'operation',
          scopedSlots: { customRender: 'operation' },
          stickyFixed: 'right'
        }
      ],
      scrollWidth: 4200,
      // 查询参数
      queryParams: {
        pageNum: 1,
        pageSize: 10
      },
      calendarIdList: []
    }
  },
  computed: {
    ...mapState({
      inventOrg: (state) => state.user.inventOrg
    })
  },
  mounted () {
    this.getCalendarIdList()
    this.onSearch();
  },
  // 状态过滤
  filters: {
    statusFilter (type) {
      return statusMap[type].text
    },
    statusTypeFilter (type) {
      return statusMap[type].status
    }
  },
  methods: {
    // 编辑
    handleEdit (record) {
      if (this.currentId && record.id !== this.currentId && this.isEdit) {
        this.$message.warning('请保存上次编辑的数据')
        return;
      }
      this.columns = JSON.parse(JSON.stringify(this.columns))
      this.isEdit = true;
      this.currentId = record.id;
    },
    // 编辑-保存
    handleSave (record) {
      if (!record.calendarId) {
        this.$message.warning('工作日历不能为空！')
        return
      }
      if (!record.masterOrganizationCode) {
        this.$message.warning('编码主组织不能为空！')
        return
      }
      if (!record.bomOrganizationCode) {
        this.$message.warning('BOM组织不能为空！')
        return
      }
      apiTSysInventoryOrganizationUpdate(record).then(response => {
        if (response.code === 200) {
          this.$message.success('保存成功')
          this.onSearch();
          this.isEdit = false;
        } else {
          this.$message.error(response.msg)
        }
      })
    },
    // 取消
    handleCancel (record) {
      this.isEdit = false
      this.onSearch();
    },
    // 表单搜索
    onSearch (values = {}) {
      this.isEdit = false
      const params = { ...values };
      delete params.searchFormLabel;
      this.$refs.dataTable.searchParams = params;
      this.$refs.dataTable.getData();
    },
    getCalendarIdList () {
      apiRoutingCalendarPullDownList().then(res => {
        if (res.code === 200 && res.data && res.data.length > 0) {
          this.calendarIdList = res.data
        }
      })
    }
  }
}
</script>

<style scoped lang="less">
.ml10{
  margin-left: 10px;
}
.table-container {
  /deep/.ant-select{
    width: 140px;
  }
}
</style>
